import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useManualProgram } from "../context/ManualProgramContext";
import { useWeeklyPlanRestore } from "../context/useWeeklyPlanRestore";
import {
  BuilderShell,
  MuscleDistributionPanel,
  PublishBar,
  PublishBarButton,
  SaveStatusPill,
  WorkoutListEditable,
} from "../features/builders";
import { GearIcon, MetricTile } from "../components/product";
import { Button, Dialog, Field, Sheet, Stepper } from "../design-v2";
import { resolveBackTarget } from "../features/weeklyPlans/navigation";
import {
  getManualBuilderWorkoutPath,
  getWeeklyPlanDetailsPath,
  getWeeklyPlansPath,
} from "../features/weeklyPlans/routes";
import { deleteWeeklyPlan, publishWeeklyPlanDraft } from "../services/api";
import {
  aggregateWorkoutMetrics,
  computeWorkoutMetrics,
} from "../utils/workoutMetrics";
import { usePlannedMuscleVolume } from "../features/muscleVolume/usePlannedMuscleVolume";

function formatMinutes(value) {
  return `${value}m`;
}

function getMetricBarWidth(value, maxValue) {
  if (value <= 0 || maxValue <= 0) {
    return "0%";
  }

  return `${Math.max(8, Math.min(100, (value / maxValue) * 100))}%`;
}

function canMoveSelectedWorkouts(workouts, selectedIds, direction) {
  const selectedIdSet = new Set(selectedIds);

  if (!selectedIdSet.size) {
    return false;
  }

  if (direction === "up") {
    return workouts.some(
      (workout, index) =>
        selectedIdSet.has(workout.id) &&
        index > 0 &&
        !selectedIdSet.has(workouts[index - 1].id)
    );
  }

  return workouts.some(
    (workout, index) =>
      selectedIdSet.has(workout.id) &&
      index < workouts.length - 1 &&
      !selectedIdSet.has(workouts[index + 1].id)
  );
}

function normalizeWorkoutName(value) {
  return String(value || "").trim().toLowerCase();
}

function validatePlanName(value) {
  return String(value || "").trim() ? "" : "Plan name is required";
}

function hasCardioBlock(workout) {
  return (workout?.blocks || []).some((block) => block.type === "cardio");
}

function isWeeklyDraftReadyForPublish(draft) {
  const workouts = draft?.workouts || [];
  const sessionsPerWeek = Number(draft?.sessionsPerWeek) || 0;
  const names = workouts.map((workout) => normalizeWorkoutName(workout.name));
  return (
    workouts.length === sessionsPerWeek &&
    workouts.every((workout) => Array.isArray(workout.blocks) && workout.blocks.length > 0) &&
    names.every(Boolean) &&
    new Set(names).size === names.length
  );
}

export default function ManualBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const manualProgram = useManualProgram();
  const {
    programDraft,
    draftMetadata,
    addWorkout,
    moveWorkouts,
    duplicateWorkouts,
    prepareWeeklyPlanDraftForPublish,
    removeWorkouts,
    updateProgramSettings,
    resetProgramDraft,
    reloadLatestAfterConflict,
    updateDraftMetadata,
  } = manualProgram;
  const { isRestoring: isRestoringPlan } = useWeeklyPlanRestore(manualProgram);
  const [showMuscleDistribution, setShowMuscleDistribution] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedWorkoutIds, setSelectedWorkoutIds] = useState([]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsProgramNameDraft, setSettingsProgramNameDraft] = useState("");
  const [settingsNameError, setSettingsNameError] = useState("");
  const [hasInteractedWithSettingsName, setHasInteractedWithSettingsName] = useState(false);
  const [settingsSessionsPerWeek, setSettingsSessionsPerWeek] = useState(4);
  const [settingsSessionsError, setSettingsSessionsError] = useState("");
  const [showDeleteProgramConfirm, setShowDeleteProgramConfirm] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isReloadingAfterConflict, setIsReloadingAfterConflict] = useState(false);
  const [showConflictReloadConfirm, setShowConflictReloadConfirm] = useState(false);
  const [isPublishingAndTransforming, setIsPublishingAndTransforming] = useState(false);
  const [publishAndTransformError, setPublishAndTransformError] = useState("");
  const [publishError, setPublishError] = useState("");
  const [deleteProgramError, setDeleteProgramError] = useState("");
  const [isDeleteBlockedByLinkedCycle, setIsDeleteBlockedByLinkedCycle] = useState(false);
  const [isDeletingProgram, setIsDeletingProgram] = useState(false);

  const programName = programDraft.programName;
  const sessionsPerWeek = programDraft.sessionsPerWeek || 4;
  const createdWorkoutCount = programDraft.workouts.length;
  const canCreateWorkout = createdWorkoutCount < sessionsPerWeek;
  const isWeeklyTemplateReady = useMemo(
    () => isWeeklyDraftReadyForPublish(programDraft),
    [programDraft]
  );
  const selectedWorkoutIdSet = useMemo(
    () => new Set(selectedWorkoutIds),
    [selectedWorkoutIds]
  );
  const canMoveUp = useMemo(
    () => canMoveSelectedWorkouts(programDraft.workouts, selectedWorkoutIds, "up"),
    [programDraft.workouts, selectedWorkoutIds]
  );
  const canMoveDown = useMemo(
    () => canMoveSelectedWorkouts(programDraft.workouts, selectedWorkoutIds, "down"),
    [programDraft.workouts, selectedWorkoutIds]
  );
  const canDuplicate =
    selectedWorkoutIds.length > 0 &&
    createdWorkoutCount + selectedWorkoutIds.length <= sessionsPerWeek;
  const canDelete = selectedWorkoutIds.length > 0;

  const workoutNameValidationById = useMemo(() => {
    const normalizedNameCounts = new Map();

    programDraft.workouts.forEach((workout) => {
      const normalizedName = normalizeWorkoutName(workout.name);

      if (!normalizedName) {
        return;
      }

      normalizedNameCounts.set(
        normalizedName,
        (normalizedNameCounts.get(normalizedName) || 0) + 1
      );
    });

    return Object.fromEntries(
      programDraft.workouts.map((workout) => {
        const normalizedName = normalizeWorkoutName(workout.name);
        const isEmptyName = normalizedName.length === 0;
        const isDuplicateName =
          !isEmptyName && (normalizedNameCounts.get(normalizedName) || 0) > 1;

        return [
          workout.id,
          {
            isEmptyName,
            isDuplicateName,
            nameError: isEmptyName
              ? "Workout name is required"
              : isDuplicateName
                ? "Workout name must be unique"
                : "",
          },
        ];
      })
    );
  }, [programDraft.workouts]);

  const weeklyMetrics = useMemo(
    () => aggregateWorkoutMetrics(programDraft.workouts),
    [programDraft.workouts]
  );
  const weeklyMuscleVolume = usePlannedMuscleVolume(programDraft.workouts);
  const topStats = useMemo(
    () => [
      {
        label: "Exercises",
        value: String(weeklyMetrics.totalExerciseCount),
        width: getMetricBarWidth(
          weeklyMetrics.totalExerciseCount,
          Math.max(1, sessionsPerWeek * 8)
        ),
      },
      {
        label: "Sets",
        value: String(weeklyMetrics.totalSetCount),
        width: getMetricBarWidth(
          weeklyMetrics.totalSetCount,
          Math.max(1, sessionsPerWeek * 20)
        ),
      },
      {
        label: "AVG. Time",
        value: formatMinutes(weeklyMetrics.averageDurationMinutes),
        width: getMetricBarWidth(weeklyMetrics.averageDurationMinutes, 120),
      },
      {
        label: "AVG. TUT",
        value: formatMinutes(weeklyMetrics.averageTUTMinutes),
        width: getMetricBarWidth(weeklyMetrics.averageTUTMinutes, 60),
      },
    ],
    [sessionsPerWeek, weeklyMetrics]
  );

  const workoutCards = useMemo(
    () =>
      programDraft.workouts.map((workout) => {
        const metrics = computeWorkoutMetrics(workout);

        return {
          ...workout,
          meta: `${metrics.exerciseCount} exercises • ${metrics.setCount} ${hasCardioBlock(workout) ? "Sets" : "sets"
            } • ~${metrics.estimatedDurationMinutes} min • ${metrics.totalTUTMinutes}m TUT`,
        };
      }),
    [programDraft.workouts]
  );

  const settingsCanSave = settingsProgramNameDraft.trim().length > 0;
  const backTarget = resolveBackTarget(location, "/program");

  const saveStatusLabel = useMemo(() => {
    if (draftMetadata.saveState === "conflict") {
      return "Update available";
    }

    if (draftMetadata.saveState === "saving") {
      return "Saving...";
    }

    if (draftMetadata.saveState === "error") {
      return "Save failed";
    }

    if (draftMetadata.lastSavedAt) {
      return "Saved";
    }

    return "Draft";
  }, [draftMetadata.lastSavedAt, draftMetadata.saveState]);

  const handleReloadAfterConflict = () => {
    if (isReloadingAfterConflict) {
      return;
    }

    setShowConflictReloadConfirm(true);
  };

  const handleConfirmReloadAfterConflict = async () => {
    setShowConflictReloadConfirm(false);
    setIsReloadingAfterConflict(true);
    try {
      await reloadLatestAfterConflict();
    } finally {
      setIsReloadingAfterConflict(false);
    }
  };

  const toggleEditMode = () => {
    setIsEditMode((prev) => {
      if (prev) {
        setSelectedWorkoutIds([]);
      }

      return !prev;
    });
  };

  const toggleWorkoutSelection = (workoutId) => {
    setSelectedWorkoutIds((prev) =>
      prev.includes(workoutId)
        ? prev.filter((id) => id !== workoutId)
        : [...prev, workoutId]
    );
  };

  const handleMove = (direction) => {
    if ((direction === "up" && !canMoveUp) || (direction === "down" && !canMoveDown)) {
      return;
    }

    moveWorkouts(selectedWorkoutIds, direction);
  };

  const handleDuplicate = () => {
    if (!canDuplicate) {
      return;
    }

    duplicateWorkouts(selectedWorkoutIds);
  };

  const handleDelete = () => {
    if (!canDelete) {
      return;
    }

    removeWorkouts(selectedWorkoutIds);
    setSelectedWorkoutIds([]);
  };

  const openSettingsPanel = () => {
    setSettingsProgramNameDraft(programDraft.programName);
    setSettingsNameError("");
    setHasInteractedWithSettingsName(false);
    setSettingsSessionsPerWeek(programDraft.sessionsPerWeek || 4);
    setSettingsSessionsError("");
    setDeleteProgramError("");
    setIsDeleteBlockedByLinkedCycle(false);
    setShowDeleteProgramConfirm(false);
    setIsSettingsOpen(true);
  };

  const closeSettingsPanel = () => {
    setIsSettingsOpen(false);
    setSettingsNameError("");
    setHasInteractedWithSettingsName(false);
    setSettingsSessionsError("");
    setDeleteProgramError("");
    setIsDeleteBlockedByLinkedCycle(false);
    setShowDeleteProgramConfirm(false);
  };

  const handleSettingsNameChange = (event) => {
    const nextValue = event.target.value;
    setSettingsProgramNameDraft(nextValue);

    if (settingsNameError && nextValue.trim()) {
      setSettingsNameError("");
    }
  };

  const handleSettingsNameBlur = () => {
    setHasInteractedWithSettingsName(true);
    setSettingsNameError(validatePlanName(settingsProgramNameDraft));
  };

  const decrementSettingsSessions = () => {
    const nextValue = Math.max(1, settingsSessionsPerWeek - 1);

    if (nextValue < createdWorkoutCount) {
      setSettingsSessionsError(
        "Delete existing workouts before reducing sessions per week"
      );
      return;
    }

    setSettingsSessionsPerWeek(nextValue);
    setSettingsSessionsError("");
  };

  const incrementSettingsSessions = () => {
    setSettingsSessionsPerWeek((prev) => Math.min(7, prev + 1));
    setSettingsSessionsError("");
  };

  const handleSaveSettings = () => {
    const nextSettingsNameError = validatePlanName(settingsProgramNameDraft);
    setHasInteractedWithSettingsName(true);
    setSettingsNameError(nextSettingsNameError);

    if (nextSettingsNameError) {
      return;
    }

    updateProgramSettings({
      programName: settingsProgramNameDraft.trim(),
      sessionsPerWeek: settingsSessionsPerWeek,
    });
    closeSettingsPanel();
  };

  const handleDeleteProgram = async () => {
    if (!draftMetadata.loadedFromBackend) {
      resetProgramDraft();
      closeSettingsPanel();
      navigate("/program");
      return;
    }

    if (!draftMetadata.weeklyPlanParentId || isDeletingProgram) {
      return;
    }

    setDeleteProgramError("");
    setIsDeleteBlockedByLinkedCycle(false);
    setIsDeletingProgram(true);

    try {
      await deleteWeeklyPlan(draftMetadata.weeklyPlanParentId);
      closeSettingsPanel();
      navigate(getWeeklyPlansPath(), { replace: true });
    } catch (error) {
      setIsDeleteBlockedByLinkedCycle(
        error?.code === "WEEKLY_PLAN_LINKED_TO_CYCLE"
      );
      setDeleteProgramError(
        error.message ||
        "Unable to delete this weekly plan."
      );
    } finally {
      setIsDeletingProgram(false);
    }
  };

  const handleBack = () => {
    navigate(backTarget);
  };

  const handlePublish = async () => {
    if (
      !draftMetadata.weeklyPlanParentId ||
      isPublishing ||
      draftMetadata.saveState === "conflict"
    ) {
      return;
    }

    setIsPublishing(true);
    setPublishError("");

    try {
      const preparation = await prepareWeeklyPlanDraftForPublish();
      if (preparation?.status !== "ready") {
        throw new Error("Unable to prepare this weekly plan for publishing.");
      }
      if (!isWeeklyDraftReadyForPublish(preparation.draft)) {
        setPublishError("Complete all workouts before publishing.");
        return;
      }
      const weeklyPlanParentId = preparation.metadata.weeklyPlanParentId;
      await publishWeeklyPlanDraft(weeklyPlanParentId);
      updateDraftMetadata({
        status: "published",
        weeklyPlanVersionId: null,
      });
      navigate(getWeeklyPlanDetailsPath(weeklyPlanParentId), {
        replace: true,
        state: {
          from: location.state?.returnTo || getWeeklyPlansPath(),
        },
      });
    } catch (error) {
      setPublishError(error.message || "Unable to publish this weekly plan.");
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublishAndTransform = async () => {
    if (
      isPublishing ||
      isPublishingAndTransforming ||
      draftMetadata.saveState === "conflict"
    ) {
      return;
    }

    if (!draftMetadata.weeklyPlanParentId) {
      setPublishAndTransformError(
        "This weekly plan must be saved before it can be published and transformed."
      );
      return;
    }

    if (!draftMetadata.weeklyPlanVersionId || draftMetadata.status !== "draft") {
      setPublishAndTransformError(
        "An editable draft is required before this weekly plan can be published and transformed."
      );
      return;
    }

    setPublishAndTransformError("");
    setIsPublishingAndTransforming(true);

    try {
      const preparation = await prepareWeeklyPlanDraftForPublish();
      if (preparation?.status !== "ready") {
        throw new Error("Unable to prepare this weekly plan for publishing.");
      }
      if (!isWeeklyDraftReadyForPublish(preparation.draft)) {
        setPublishAndTransformError("Complete all workouts before publishing.");
        return;
      }
      await publishWeeklyPlanDraft(preparation.metadata.weeklyPlanParentId);
      updateDraftMetadata({
        status: "published",
        weeklyPlanVersionId: null,
      });
      navigate("/program/manual-convert", {
        state: {
          from: `${location.pathname}${location.search || ""}`,
        },
      });
    } catch (error) {
      setPublishAndTransformError(
        error.message ||
        "Unable to publish and transform this weekly plan."
      );
    } finally {
      setIsPublishingAndTransforming(false);
    }
  };

  const publishAndTransformDisabled =
    !isWeeklyTemplateReady ||
    isPublishing ||
    isPublishingAndTransforming ||
    draftMetadata.saveState === "conflict";

  if (isRestoringPlan) {
    // The plan in the URL is being restored: never show the empty default template.
    return (
      <BuilderShell>
        <main role="status" aria-busy="true" className="lz-builder-page">
          Loading program…
        </main>
      </BuilderShell>
    );
  }

  return (
    <BuilderShell>
      <header className="lz-builder-header">
        <button
          type="button"
          onClick={handleBack}
          className="lz-builder-header__button"
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <div className="lz-builder-header__identity">
          <h1 className="lz-builder-header__title">{programName}</h1>
          <div className="lz-builder-header__meta">
            <span className="lz-builder-header__status">Draft</span>
            <SaveStatusPill label={saveStatusLabel} />
          </div>
        </div>

        <Button
          variant="secondary"
          size="icon"
          onClick={openSettingsPanel}
          aria-label="Open plan settings"
        >
          <GearIcon />
        </Button>
      </header>

      <main className="lz-builder-page">
        <div className="lz-builder-page__stack">
          {draftMetadata.saveState === "conflict" ? (
            <div className="lz-builder-alert lz-builder-alert--danger">
              <p className="lz-builder-alert__title">This plan was updated elsewhere.</p>
              <p className="lz-builder-alert__body">
                Your unsaved changes are not being saved while this is unresolved.
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleReloadAfterConflict}
                disabled={isReloadingAfterConflict}
                className="lz-builder-alert__action"
              >
                {isReloadingAfterConflict ? "Reloading..." : "Reload latest version"}
              </Button>
            </div>
          ) : null}

          <section className="lz-builder-summary lz-builder-weekly-summary">
            <div className="lz-builder-summary__heading">
              <div>
                <h2>Weekly Template</h2>
                <p>{sessionsPerWeek} Sessions/week</p>
              </div>
              <span className="lz-builder-summary__duration">
                {createdWorkoutCount}/{sessionsPerWeek} built
              </span>
            </div>
            <div
              className="lz-builder-weekly-summary__progress"
              role="progressbar"
              aria-label="Workouts created"
              aria-valuemin="0"
              aria-valuemax={sessionsPerWeek}
              aria-valuenow={Math.min(createdWorkoutCount, sessionsPerWeek)}
            >
              {Array.from({ length: sessionsPerWeek }).map((_, index) => (
                <span
                  key={index}
                  data-complete={index < createdWorkoutCount ? "true" : "false"}
                />
              ))}
            </div>
          </section>

          <div className="lz-product-metrics lz-product-metrics--section lz-product-metrics--aligned">
            {topStats.map((item) => (
              <MetricTile
                key={item.label}
                label={item.label}
                value={item.value}
                barValue={Number.parseFloat(item.width) || 0}
                barMax={100}
              />
            ))}
          </div>

          <MuscleDistributionPanel
            title="Weekly Muscle Distribution"
            volume={weeklyMuscleVolume}
            expanded={showMuscleDistribution}
            onExpandedChange={setShowMuscleDistribution}
            bodyId="weekly-muscle-distribution"
          />

          <WorkoutListEditable
            workouts={workoutCards}
            workoutNameValidationById={workoutNameValidationById}
            selectedWorkoutIds={selectedWorkoutIds}
            selectedWorkoutIdSet={selectedWorkoutIdSet}
            isEditMode={isEditMode}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
            canDuplicate={canDuplicate}
            canDelete={canDelete}
            canCreateWorkout={canCreateWorkout}
            nextWorkoutNumber={createdWorkoutCount + 1}
            onToggleEditMode={toggleEditMode}
            onToggleWorkoutSelection={toggleWorkoutSelection}
            onMoveUp={() => handleMove("up")}
            onMoveDown={() => handleMove("down")}
            onDuplicate={handleDuplicate}
            onDelete={handleDelete}
            onOpenWorkout={(workoutId) =>
              navigate(getManualBuilderWorkoutPath(workoutId, draftMetadata.weeklyPlanParentId), {
                state: {
                  from: `${location.pathname}${location.search || ""}`,
                },
              })
            }
            onAddWorkout={addWorkout}
          />

          <section className="lz-builder-transform">
            <Button
              variant="secondary"
              className="lz-builder-transform__button"
              disabled={publishAndTransformDisabled}
              isLoading={isPublishingAndTransforming}
              loadingLabel="Publishing & transforming..."
              onClick={handlePublishAndTransform}
            >
              Turn into multi-week program
            </Button>
            {publishAndTransformError ? (
              <p className="lz-builder-transform__error" role="alert">
                {publishAndTransformError}
              </p>
            ) : null}
            <p className="lz-builder-transform__help">
              Transforming to weekly program allows plan modifications across
              multiple weeks.
            </p>
          </section>
        </div>
      </main>

      <PublishBar>
        {publishError ? (
          <div className="lz-builder-alert lz-builder-alert--danger">
            <p className="lz-builder-alert__title">Publish failed</p>
            <p className="lz-builder-alert__body">{publishError}</p>
          </div>
        ) : null}
        <PublishBarButton
          disabled={
            !draftMetadata.weeklyPlanParentId ||
            !isWeeklyTemplateReady ||
            isPublishing ||
            draftMetadata.saveState === "conflict"
          }
          onClick={handlePublish}
        >
          <span>{isPublishing ? "Publishing..." : "Publish Program"}</span>
        </PublishBarButton>
      </PublishBar>

      <Sheet
        open={isSettingsOpen}
        onClose={closeSettingsPanel}
        title="Plan Settings"
        closeLabel="Close plan settings"
      >
        <div className="lz-builder-settings">
          <Field
            label="Plan Name"
            optionalLabel={null}
            error={hasInteractedWithSettingsName ? settingsNameError : ""}
          >
            <input
              type="text"
              value={settingsProgramNameDraft}
              onChange={handleSettingsNameChange}
              onBlur={handleSettingsNameBlur}
            />
          </Field>

          <div className="lz-builder-settings__section">
            <Stepper
              label="Sessions Per Week"
              value={settingsSessionsPerWeek}
              unit="Sessions"
              onDecrement={decrementSettingsSessions}
              onIncrement={incrementSettingsSessions}
              canDecrement={settingsSessionsPerWeek > 1}
              canIncrement={settingsSessionsPerWeek < 7}
              decrementLabel="Decrease sessions per week"
              incrementLabel="Increase sessions per week"
            />
            <p className="lz-builder-settings__help">
              You can&apos;t go below the number of workouts already created.
            </p>
            {settingsSessionsError ? (
              <p className="lz-builder-settings__error" role="alert">
                {settingsSessionsError}
              </p>
            ) : null}
          </div>

          <div className="lz-builder-settings__actions">
            <Button variant="secondary" onClick={closeSettingsPanel}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={!settingsCanSave}>
              Save
            </Button>
          </div>

          {!showDeleteProgramConfirm ? (
            <Button
              variant="danger"
              onClick={() => setShowDeleteProgramConfirm(true)}
              className="lz-builder-settings__danger"
            >
              Delete Program
            </Button>
          ) : null}
        </div>
      </Sheet>

      <Dialog
        open={showConflictReloadConfirm}
        onClose={() => setShowConflictReloadConfirm(false)}
        title="Reload latest version?"
        description="Your unsaved local changes will be discarded."
        size="sm"
      >
        <div className="lz-builder-dialog-actions">
          <Button variant="secondary" onClick={() => setShowConflictReloadConfirm(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmReloadAfterConflict}>Reload latest version</Button>
        </div>
      </Dialog>

      <Dialog
        open={showDeleteProgramConfirm}
        onClose={() => setShowDeleteProgramConfirm(false)}
        title={draftMetadata.loadedFromBackend ? "Delete this weekly plan?" : "Delete this draft program?"}
        description={
          draftMetadata.loadedFromBackend
            ? "Are you sure you want to delete this weekly plan? This action can't be undone."
            : "Delete this draft program? This action can't be undone."
        }
        size="sm"
      >
        {deleteProgramError ? (
          <p className="lz-builder-settings__error" role="alert">
            {deleteProgramError}
          </p>
        ) : null}
        <div className="lz-builder-dialog-actions">
          <Button variant="secondary" onClick={() => setShowDeleteProgramConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteProgram}
            disabled={isDeletingProgram || isDeleteBlockedByLinkedCycle}
            isLoading={isDeletingProgram}
            loadingLabel="Deleting..."
          >
            Delete Program
          </Button>
        </div>
      </Dialog>
    </BuilderShell>
  );
}
